import axios from 'axios';
import clsx from 'clsx';
import { MobileMenuIcon } from 'header/header/iconarea/MobileMenuIcon';
import { SrchInput } from 'header/header/srchinput/SrchInput';
import { RenderUserPopup } from 'header/header/usermenu/RenderUserPopup';
import { LoginInputValues, useZstdHeader } from 'header/header/useZstdHeader';
import useDocumentClick from 'hooks/useDocumentClick';
import { useLoginInfo, useSetLoginInfo } from 'hooks/useLoginInfo';
import { useRefCtx } from 'hooks/useRefCtx';
import { langutil } from 'language/langutil';
import { isEqual } from 'lodash-es';
import { DisplayPopupItem } from 'models/IfDisplayPopupItem';
import { IfLoginInfo } from 'models/IfLoginInfo';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import svcs from 'services';
import { useDisplayBottomPopup, useDisplayPopupItem } from 'store/reducers/useDisplayPopup';
import styled from 'styled-components';
import { loginutil } from 'util/loginutil';
import { roleutil } from 'util/roleutil';
import { useZstdInfo } from 'zstd/useZstdInfo';
import { useWinResize } from 'zstd/useZstdWinResize';
import { LOGIN_TYPE_GOOGLE, LOGIN_TYPE_KAKAO, LOGIN_TYPE_NAVER } from '../definitions/LoginType';
import { SocialSignupInfo } from '../login/LoginService';
import { useExistSsqAccountNotSnsStore } from '../store/reducers/rootReducer';
import img_beyondx_ssq from './BeyondX_SSQ.svg';
import img_beyondx_ssq_mobile from './BeyondX_SSQ_mobile.svg';
import { HeaderIconArea } from './HeaderIconArea';

interface Props {
  hideHeader?: boolean;
  absolute?: boolean;
}

const REFRESH_IS_ADMIN_CHECK_TIME = 3000;

const Header: React.FC<Props> = (props) => {
  const { hideHeader, absolute } = props;
  const location = useLocation();
  const locationState = location.state as { email?: string; socialId?: string; loginType?: string } | null;

  const { setDisplayPopupItem } = useDisplayPopupItem();

  const [isExistSsqAccountNotSnsStore] = useExistSsqAccountNotSnsStore();
  const { setDisplayBottomPopup } = useDisplayBottomPopup();

  const loginInfo = useLoginInfo();
  const setLoginInfo = useSetLoginInfo();

  const { setShowLoginPopup, setShowUserPopup, setShowSignupSocialPopup, showUserPopup, showSearchBoxPopup } =
    useZstdInfo();

  const {
    isLoadingUserPopup,
    countrySelect,
    alertNewAlarm,
    setSocialSignupInfo,
    setShowLoginFailPopup,
    setCountrySelect,
  } = useZstdHeader();

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // 모바일 메뉴 열림 시 헤더 내부 클릭으로는 메뉴가 닫히지 않도록 MainMenu의 외부클릭 예외 대상으로 전달한다.
  const headerRef = useRef<HTMLDivElement>(null);

  const refLoginInfo = useRefCtx(loginInfo);

  useEffect(() => {
    const initializeLoginInfo = async () => {
      const info: IfLoginInfo = (await axios.get('/api/guest/loginInfo'))?.data;
      if (info) {
        if (!isEqual(info, loginInfo)) {
          setLoginInfo(info);
          if (loginutil.islogin(info)) {
            await checkNewAlarmCount(info.userId);
            roleutil.hasPartnerChecks(info.userId, svcs.svcLang.lang()).then((res) => {
              if (res === true) useZstdHeader.setState({ isPartner: res });
            });
          }
        }
      }
    };

    const handleSocialLogin = async () => {
      if (isExistSsqAccountNotSnsStore) {
        const loginInputValues: LoginInputValues = {
          id: locationState?.email,
          password: locationState?.socialId,
        };
        showLoginFailPopupFunc(true, loginInputValues);
      }

      if (await isNeedSocialSignupFromRequireLoginPage()) {
        showSocialSignupPopupFunc();
      }
    };

    const checkBrowser = () => {
      if (isBrowserIE()) {
        window.location.href = '/unsupportedBrowserPage';
      }
    };

    const setupInterval = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      intervalIdRef.current = setInterval(async () => {
        if (refLoginInfo.current?.userId) return;
        try {
          const res = await axios.get('/api/guest/loginInfo');
          const info: IfLoginInfo = res.data;
          if (!isEqual(info, refLoginInfo.current)) {
            setLoginInfo(info);
            setDisplayPopupItem({ ...new DisplayPopupItem(), center: true });
            //setDisplayPopupItem({ ...displayPopupItem, center: true });
            //setDisplayBottomPopup(false);
            setDisplayBottomPopup(true);
            if (info?.userId) {
              await checkNewAlarmCount(info.userId);
            }
          }
        } catch (e) {
          console.error('E', e);
        }
      }, REFRESH_IS_ADMIN_CHECK_TIME);
    };

    initializeLoginInfo().then();
    handleSocialLogin().then();
    checkBrowser();
    setupInterval();

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== location.pathname) {
      useZstdInfo.setState({ showUserPopup: false });
    }
  }, [location.pathname]);

  const isNeedSocialSignupFromRequireLoginPage = async () => {
    const loginType = location.state?.loginType;
    const email = location.state?.email ? location.state?.email : '';

    const isAlreadyMember = await svcs.repoAccount.isExistAccountId(email);
    if (
      isAlreadyMember === false &&
      (loginType === LOGIN_TYPE_GOOGLE || loginType === LOGIN_TYPE_KAKAO || loginType === LOGIN_TYPE_NAVER)
    ) {
      return true;
    }

    return false;
  };

  const isBrowserIE = (): boolean => {
    const agent = navigator.userAgent.toLowerCase();

    if (
      (navigator.appName === 'Netscape' && navigator.userAgent.search('Trident') !== -1) ||
      agent.indexOf('MSIE') !== -1
    ) {
      return true;
    }
    return false;
  };

  const setLoggedInStatus = async (info: IfLoginInfo) => {
    if (info.userId) await checkNewAlarmCount(info.userId);
  };

  const checkNewAlarmCount = async (loggedInAccountId) => {
    axios
      .post('/api/member/ssqData/user/alarm/count/new', {
        userId: loggedInAccountId,
      })
      .then((res) => {
        useZstdHeader.setState({ alertAlarmCount: res.data, alertNewAlarm: res.data > 0 ? true : false });
      });
  };

  const showLoginFailPopupFunc = (showLoginFailPopup: boolean, loginInputValues?: LoginInputValues) => {
    setShowLoginPopup(true);
    setShowLoginFailPopup(showLoginFailPopup);

    if (loginInputValues) {
      useZstdHeader.setState({ loginInputValues });
    }
  };

  const openUserPopup = async (showUserPopup) => {
    if (!showUserPopup) {
      await useZstdHeader.setState({
        isLoadingUserPopup: true,
        countrySelect: false,
      });
      setShowUserPopup(!showUserPopup);

      const info: IfLoginInfo = (await axios.get('/api/guest/loginInfo')).data;
      await setLoggedInStatus(info);

      useZstdHeader.setState({ isLoadingUserPopup: false });
    } else {
      useZstdHeader.setState({ isLoadingUserPopup: false });
      useZstdHeader.setState({ countrySelect: false });
      setShowUserPopup(!showUserPopup);
    }
  };

  const openCountryPopup = (countrySelect: boolean) => {
    useZstdHeader.setState({ countrySelect: !countrySelect });
    setShowUserPopup(false);
    console.log('countrySelect', countrySelect);
  };

  const showSocialSignupPopupFunc = () => {
    const signupInfo: SocialSignupInfo = {
      email: locationState?.email,
      socialId: locationState?.socialId,
      loginType: locationState?.loginType,
    };
    setShowSignupSocialPopup(true);
    setSocialSignupInfo(signupInfo);
  };

  const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());

  useDocumentClick(() => {
    setCountrySelect(false);
    setShowUserPopup(false);
  }, []);

  const { isMobile, isTablet } = useWinResize();

  return (
    <>
      <HeaderContainer
        ref={headerRef}
        $showSearchBoxPopup={showSearchBoxPopup}
        aria-label='header-container'
        className={clsx({ absolute })}
        onClick={() => {
          setShowUserPopup(false);
          setCountrySelect(false);
        }}
        style={{ display: hideHeader && 'none' }}>
        <HeaderContents className={'container'}>
          <LogoArea>
            <a href={`/${country}/${lang}/main`}>
              {/* <img alt={"ssq_logo.svg"} src={SsqImages.common.logo.lsHeader()}/>
              <span className="solution-square">Solution Square</span> */}
              {isMobile || isTablet ? (
                <img alt={'beyondx_ssq_logo.svg'} src={img_beyondx_ssq_mobile} style={{ width: '16.3rem' }} />
              ) : (
                <img alt={'beyondx_ssq_logo.svg'} src={img_beyondx_ssq} style={{ width: '24.245rem' }} />
              )}
            </a>
          </LogoArea>
          {/* 검색창을 사이에 두고 좌우로 동일한 flex:1 스페이서를 배치해 검색창을 가운데 정렬한다(너비를 늘리면 좌우 대칭 확장).
              검색창이 없는 화면(예: 클래식 뷰)에서는 이 스페이서가 늘어나 아이콘 영역을 항상 오른쪽 끝에 고정한다. */}
          {!isMobile && <HeaderFlexSpacer />}
          {!isMobile && (
            <>
              <SrchInput />
              <HeaderFlexSpacer />
            </>
          )}

          <HeaderIconArea
            openCountryPopup={openCountryPopup}
            countrySelect={countrySelect}
            main={false}
            checkNewAlarmCount={checkNewAlarmCount}
            openUserPopup={openUserPopup}
            showUserPopup={showUserPopup}
            isLoadingUserPopup={isLoadingUserPopup}
            alertNewAlarm={alertNewAlarm}
          />
          <MobileMenuIcon main={false} headerRef={headerRef} />
        </HeaderContents>
        <React.Suspense fallback={<div></div>}>
          <RenderUserPopup main={false} />
        </React.Suspense>
      </HeaderContainer>
    </>
  );
};

export default Header;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 검색창 좌/우 공통 가변 스페이서: 좌우에 동일하게 배치해 검색창을 가운데 정렬한다. 검색창이 없을 땐 좌측 것만 남아 아이콘 영역을 오른쪽 끝에 고정한다.
const HeaderFlexSpacer = styled.div`
  flex: 1;
`;

const HeaderContainer = styled.div<{ $showSearchBoxPopup: boolean }>`
  &.absolute {
    position: fixed;
    top: 0;
    left: 0;
  }

  .allpopupwrap > div {
    transform: inherit;
    top: calc(50% - (64rem / 2));
    left: calc(50% - (36rem / 2));
  }

  z-index: 999;
  background-color: white;
  border-bottom: 0.5px solid #b2b2b2;
  width: 100%;
  //box-shadow: 0px 4px 18px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;

  @media screen and (max-width: 764px) {
    ${({ $showSearchBoxPopup }) => ($showSearchBoxPopup ? 'display: none;' : '')}
    border-bottom: 1px solid #eef1f6;
    .container {
      height: 100%;
      padding: 5.2rem 2rem 1.8rem;
    }
  }
`;

const HeaderContents = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 1rem;
  //height: 6rem;
  min-height: 6rem;
  height: 11.4rem;
  position: relative;
  z-index: 102;
  background-color: white;

  a {
    text-decoration: none;
    color: #2a2c2f;
  }

  @media screen and (max-width: 764px) {
    padding: 0;
    justify-content: space-between;
  }
`;
